import { config } from '@/config'
import { es } from '@/locales/es'

import { ApiErrorCode, type ApiErrorBody } from './types'

/**
 * HTTP client for the API.
 *
 * Responsibilities, and deliberately nothing else:
 *   - prefix the base URL
 *   - attach the Entra ID bearer token
 *   - turn a failed response into a typed error with a Spanish message
 *   - let the app react to 401 and 403 in one place
 *
 * It does not cache, retry or deduplicate: TanStack Query does that.
 */

/** Requests that outlive this are aborted rather than left hanging. */
const DEFAULT_TIMEOUT_MS = 15_000

/**
 * A failed request, carrying the backend's code so callers can branch on it
 * and a message already written in Spanish for display.
 */
export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly details?: Record<string, unknown>
  /** Correlates with the backend log line for the same request. */
  readonly requestId?: string

  constructor(args: {
    code: string
    message: string
    status: number
    details?: Record<string, unknown>
    requestId?: string
  }) {
    super(args.message)
    this.name = 'ApiError'
    this.code = args.code
    this.status = args.status
    this.details = args.details
    this.requestId = args.requestId
  }

  /** True when the user is not registered in the system or lacks the permission. */
  get isForbidden(): boolean {
    return this.status === 403
  }

  /** True when the token is missing, invalid or expired. */
  get isUnauthorized(): boolean {
    return this.status === 401
  }
}

// --- Token provider ----------------------------------------------------

export interface TokenOptions {
  /** Skip the cache and ask the identity provider for a new token (used after a 401). */
  forceRefresh?: boolean
}

type TokenProvider = (options?: TokenOptions) => Promise<string | null>

/**
 * Returns null in dev mode (no MSAL): requests then go out without an
 * Authorization header, which the backend accepts in `dev-bypass` mode.
 */
let getToken: TokenProvider = async () => null

/**
 * Registers how the client obtains the access token.
 *
 * Kept injectable so the client has no dependency on MSAL: it can be used
 * and tested before authentication exists, and swapping the identity
 * provider later touches one function instead of every call site.
 */
export function setTokenProvider(provider: TokenProvider): void {
  getToken = provider
}

/** Current access token, for requests that cannot go through `request` (uploads with progress). */
export async function getAccessToken(options?: TokenOptions): Promise<string | null> {
  return getToken(options)
}

// --- Global handlers ---------------------------------------------------

type ErrorHandler = (error: ApiError) => void

let onUnauthorized: ErrorHandler = () => {}
let onForbidden: ErrorHandler = () => {}

/**
 * Registers what the app does on 401 and 403.
 *
 * Both live here so the reaction is defined once, instead of every screen
 * deciding for itself what an expired session looks like.
 */
export function setAuthErrorHandlers(handlers: {
  onUnauthorized?: ErrorHandler
  onForbidden?: ErrorHandler
}): void {
  if (handlers.onUnauthorized) onUnauthorized = handlers.onUnauthorized
  if (handlers.onForbidden) onForbidden = handlers.onForbidden
}

// --- Request -----------------------------------------------------------

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  /** Serialized as JSON. Omit for GET. */
  body?: unknown
  /** Appended as a query string; undefined and null entries are dropped. */
  query?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
  timeoutMs?: number
}

/**
 * Performs a request and returns the parsed body.
 *
 * Throws ApiError on any failure, including network problems, so callers
 * have exactly one error type to handle.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, timeoutMs = DEFAULT_TIMEOUT_MS } = options

  const url = new URL(config.apiBaseUrl + path)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value))
      }
    }
  }

  const send = async (token: string | null): Promise<Response> => {
    const headers = new Headers({ Accept: 'application/json' })
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }
    if (body !== undefined) {
      headers.set('Content-Type', 'application/json')
    }

    // The caller's signal and the timeout both have to be able to abort.
    const timeout = AbortSignal.timeout(timeoutMs)
    const combined = signal ? AbortSignal.any([signal, timeout]) : timeout

    return fetch(url, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: combined,
    })
  }

  let response: Response
  try {
    const token = await getToken()
    response = await send(token)

    // A 401 with a token usually means it expired or was revoked between
    // the cache check and the server: renew once and retry once. If it
    // fails again the global handler sends the user to sign in.
    if (response.status === 401 && token) {
      const renewed = await getToken({ forceRefresh: true })
      if (renewed) response = await send(renewed)
    }
  } catch (cause) {
    // fetch only rejects on network failure or abort; an HTTP error status
    // is a resolved promise.
    // A timeout and an unreachable server are different failures and the
    // user can act differently on each: one is worth retrying right away.
    const timedOut = cause instanceof DOMException && cause.name === 'TimeoutError'
    throw new ApiError({
      code: timedOut ? ApiErrorCode.Timeout : ApiErrorCode.Network,
      message: timedOut ? es.errors.timeout : es.errors.network,
      status: 0,
    })
  }

  const requestId = response.headers.get('X-Request-Id') ?? undefined

  if (!response.ok) {
    throw await toApiError(response, requestId)
  }

  // 204 and empty bodies are valid successes.
  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return undefined as T
  }

  try {
    return (await response.json()) as T
  } catch {
    throw new ApiError({
      code: ApiErrorCode.Internal,
      message: es.errors.generic,
      status: response.status,
      requestId,
    })
  }
}

/**
 * Builds the error for a failed response and runs the global handler.
 *
 * The backend's message is used as-is: it is already written in Spanish for
 * the user. A response without the expected envelope falls back to a generic
 * message rather than showing the raw body, which could be an HTML error
 * page from a proxy.
 */
export async function toApiError(response: Response, requestId?: string): Promise<ApiError> {
  // Annotated as string: the locale file is `as const`, so inference would
  // pin these to the literal of their initial value.
  let code: string = ApiErrorCode.Internal
  let message: string = es.errors.generic
  let details: Record<string, unknown> | undefined

  try {
    const parsed = (await response.json()) as { error?: unknown; code?: unknown }
    if (typeof parsed?.error === 'string') {
      // Plans endpoints answer `{ "error": "mensaje" }` (see backend/docs/CONTRATO-CICLO-PLANOS.md),
      // plus a machine `code` ("unauthorized", "forbidden", "out_of_scope") on 401/403.
      message = parsed.error || message
      if (typeof parsed.code === 'string' && parsed.code) code = parsed.code
    } else {
      const envelope = (parsed as Partial<ApiErrorBody>)?.error
      if (envelope?.code) {
        code = envelope.code
        message = envelope.message || message
        details = envelope.details
      }
    }
  } catch {
    // Not JSON. Keep the generic message.
  }

  if (response.status === 401) {
    message = es.errors.unauthorized
  }

  const error = new ApiError({ code, message, status: response.status, details, requestId })

  if (error.isUnauthorized) onUnauthorized(error)
  if (error.isForbidden) onForbidden(error)

  return error
}

/** Convenience wrappers. */
export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}
